import React, { useState } from 'react';
import { ethers } from 'ethers';
import Input from '../../components/common/Input/Input';
import Button from '../../components/common/Button/Button';
import { useNotification } from '../../context/NotificationContext';
import { Section, PackageSelect, PositionSelect } from './AdminUI';
import { toWei } from './useAdmin';
import { DEFAULT_REFERRER_ID } from '../../config/packages';
import { formatUserId } from '../../utils/formatters';

// Same range as Logic._generateId: 1000 .. 9999999
const randomId = () => Math.floor(Math.random() * 9999000) + 1000;

const AdminRegister = ({ admin }) => {
  const { read, runTx, pending } = admin;
  const { error: showError } = useNotification();

  // Auto ID + auto placement
  const [auto, setAuto] = useState({ wallet: '', sponsor: '', pos: '0', pkg: '10' });
  // Specific ID + placement
  const [manual, setManual] = useState({ wallet: '', userId: '', sponsor: '', placement: '', pos: '0', pkg: '10' });
  const [preparing, setPreparing] = useState(false);

  const checkWalletFree = async (wallet) => {
    if (!ethers.utils.isAddress(wallet)) {
      showError('Invalid wallet address');
      return false;
    }
    const existing = (await read.storage.addressToId(wallet)).toNumber();
    if (existing) {
      showError(`Wallet already registered as ${formatUserId(existing)}`);
      return false;
    }
    return true;
  };

  // Returns the resolved sponsor ID, or null if invalid
  const resolveSponsor = async (input) => {
    const id = parseInt(input || '0', 10) || DEFAULT_REFERRER_ID;
    const s = await read.storage.getUser(id);
    if (!s.active) {
      showError(`Sponsor ${formatUserId(id)} is not an active user`);
      return null;
    }
    return id;
  };

  // Mirrors Logic._findPlacement: follow the outer leg until an empty slot
  const findPlacement = async (start, pos) => {
    let curr = start;
    for (let i = 0; i < 10000; i++) {
      const u = await read.storage.getUser(curr);
      const next = (pos === 0 ? u.leftChild : u.rightChild).toNumber();
      if (next === 0) return curr;
      curr = next;
    }
    return null;
  };

  const findFreeId = async () => {
    for (let i = 0; i < 20; i++) {
      const id = randomId();
      if (!(await read.storage.usedIds(id))) return id;
    }
    return null;
  };

  // adminRegisterWithId skips its slot check when placement == sponsor, so an occupied
  // slot would be overwritten in that case - always check here first
  const checkSlotFree = async (placement, pos) => {
    const p = await read.storage.getUser(placement);
    if (!p.active) {
      showError(`Placement ${formatUserId(placement)} is not an active user`);
      return false;
    }
    const slot = pos === 0 ? p.leftChild : p.rightChild;
    if (!slot.isZero()) {
      showError(`Placement ${formatUserId(placement)} ${pos === 0 ? 'left' : 'right'} slot is taken by ${formatUserId(slot)}`);
      return false;
    }
    return true;
  };

  const submitAuto = async () => {
    const pos = parseInt(auto.pos, 10);
    setPreparing(true);
    let userId, sponsor, placement;
    try {
      if (!(await checkWalletFree(auto.wallet))) return;
      sponsor = await resolveSponsor(auto.sponsor);
      if (!sponsor) return;

      placement = await findPlacement(sponsor, pos);
      if (!placement) return showError('No placement found');

      userId = await findFreeId();
      if (!userId) return showError('Could not find a free user ID, try again');
    } catch (err) {
      console.error('Auto register prepare error:', err);
      return showError('Could not prepare registration');
    } finally {
      setPreparing(false);
    }

    const ok = await runTx('vreg', `Registration #${userId}`, (w) =>
      w.logic.adminRegisterWithId(auto.wallet, userId, sponsor, placement, pos, toWei(auto.pkg))
    );
    if (ok) setAuto({ wallet: '', sponsor: '', pos: '0', pkg: '10' });
  };

  const submitManual = async () => {
    const userId = parseInt(manual.userId, 10);
    const placement = parseInt(manual.placement, 10);
    const pos = parseInt(manual.pos, 10);

    if (!userId || userId < 1) return showError('Enter a valid user ID');
    if (!placement) return showError('Enter a placement ID');
    if (!(await checkWalletFree(manual.wallet))) return;
    const sponsor = await resolveSponsor(manual.sponsor);
    if (!sponsor) return;

    if (await read.storage.usedIds(userId)) return showError(`User ID #${userId} is already in use`);
    if (!(await checkSlotFree(placement, pos))) return;

    const ok = await runTx('idreg', 'Registration', (w) =>
      w.logic.adminRegisterWithId(manual.wallet, userId, sponsor, placement, pos, toWei(manual.pkg))
    );
    if (ok) setManual({ wallet: '', userId: '', sponsor: '', placement: '', pos: '0', pkg: '10' });
  };

  return (
    <>
      <Section title="Virtual Register" subtitle="Random ID, auto placement on the outer leg of the sponsor. No tokens are transferred.">
        <Input label="Wallet" placeholder="0x..." value={auto.wallet} onChange={(e) => setAuto({ ...auto, wallet: e.target.value.trim() })} />
        <Input label="Sponsor ID" placeholder={`Blank = ${DEFAULT_REFERRER_ID}`} type="number" value={auto.sponsor} onChange={(e) => setAuto({ ...auto, sponsor: e.target.value })} />
        <div className="admin-row-2">
          <PositionSelect value={auto.pos} onChange={(v) => setAuto({ ...auto, pos: v })} />
          <PackageSelect value={auto.pkg} onChange={(v) => setAuto({ ...auto, pkg: v })} />
        </div>
        <Button fullWidth onClick={submitAuto} loading={preparing || pending === 'vreg'}>Register</Button>
      </Section>

      <Section title="Register with Specific ID" subtitle="Choose the user ID and exact placement. Virtual - no tokens are transferred.">
        <Input label="Wallet" placeholder="0x..." value={manual.wallet} onChange={(e) => setManual({ ...manual, wallet: e.target.value.trim() })} />
        <div className="admin-row-2">
          <Input label="User ID" type="number" value={manual.userId} onChange={(e) => setManual({ ...manual, userId: e.target.value })} />
          <Input label="Sponsor ID" placeholder={`Blank = ${DEFAULT_REFERRER_ID}`} type="number" value={manual.sponsor} onChange={(e) => setManual({ ...manual, sponsor: e.target.value })} />
        </div>
        <div className="admin-row-2">
          <Input label="Placement ID" type="number" value={manual.placement} onChange={(e) => setManual({ ...manual, placement: e.target.value })} />
          <PositionSelect value={manual.pos} onChange={(v) => setManual({ ...manual, pos: v })} />
        </div>
        <PackageSelect value={manual.pkg} onChange={(v) => setManual({ ...manual, pkg: v })} />
        <Button fullWidth onClick={submitManual} loading={pending === 'idreg'}>Register</Button>
      </Section>
    </>
  );
};

export default AdminRegister;
